import {
  type ClientSecretPointer,
  toClientSecretPointer,
} from "@p0security/integrations-tailscale-shared/credential";
import {
  type ConnectorIdentity,
  toConnectorIdentity,
} from "@p0security/integrations-tailscale-shared/identity";
import { TAILSCALE_SCOPES } from "@p0security/integrations-tailscale-shared/scopes";
import type {
  TailscaleConnectorHosting,
  TailscaleCredentialConfig,
} from "@p0security/integrations-tailscale-shared/types";
import { tfId } from "@p0security/integrations-vaulted-credential-shared";
import { assertNever } from "@p0security/util/assertNever";

import { toHclComment } from "./hcl.ts";

// The read grant copies Auth0's tenant Terraform (app#7529 @ 3c8d887b9a)
// instead of sharing it, so adding Tailscale changes no other integration.

type AwsIdentity = Extract<ConnectorIdentity, { type: "aws" }>;
type GcpIdentity = Extract<ConnectorIdentity, { type: "gcp" }>;
type OAuthClient = Extract<TailscaleCredentialConfig, { type: "oauth-client" }>;
type AwsVault = Extract<OAuthClient["vault"], { type: "aws-sm" }>;

export type ReadGrantArgs = {
  /** The install item id of the tailnet's connector */
  connectorId: string;
  hosting: TailscaleConnectorHosting;
  /** The Tailnet ID, which is its install item's id */
  tailnet: string;
  credential: Pick<OAuthClient, "clientSecretName" | "vault">;
};

/** The configuration for the secret's own AWS account, when it isn't the
 * connector's. */
type VaultAccountBlock = {
  purpose: "vault-account";
  accountId: string;
  terraform: string;
};

/** One Terraform configuration of a tailnet's read grant, and where the
 * customer applies it. */
export type ReadGrantBlock =
  | VaultAccountBlock
  | { purpose: "grant"; terraform: string };

export const isVaultAccountBlock = (
  block: ReadGrantBlock
): block is VaultAccountBlock => block.purpose === "vault-account";

/** The Terraform name of a tailnet's resources. One tailnet's names mustn't
 * clash with a connector's in the same configuration.
 *
 * Every character of the ID other than a letter, digit or `_` becomes `_`,
 * such as the `@` and `.` of a legacy Tailnet ID like `alice@gmail.com`,
 * since Terraform names take no such character. Two tailnets whose IDs differ
 * only by those get the same names here, and can't share a configuration:
 * Terraform refuses the second's blocks. */
export const toTailnetTfId = (tailnet: string) =>
  tfId(`p0_tailscale_tailnet_${tailnet.replace(/[^A-Za-z0-9_]/g, "_")}`);

// IAM's limit on the name of a role's inline policy.
const POLICY_NAME_MAX_LENGTH = 128;

/** The name of the inline policy that a tailnet adds to its connector's
 * role, which holds one for each tailnet with an OAuth client.
 *
 * A Tailnet ID can be longer than IAM lets a policy's name be, so this cuts
 * the name to fit. Two tailnets on one connector whose IDs start with the
 * same 99 characters would then share a policy. */
const toPolicyName = (tailnet: string) =>
  `ReadTailscaleOAuthClientSecret-${tailnet}`.slice(0, POLICY_NAME_MAX_LENGTH);

/** Where a tailnet's OAuth client secret is, next to its connector on AWS. */
type AwsVaultPlacement = {
  /** Whether the secret is in another account than the connector */
  crossAccount: boolean;
  vaultAccountId: string;
};

/** The comment on the setting for the KMS key's ARN, as `#` lines that each
 * line after the first starts with `indent`. */
const renderKmsKeyComment = ({
  crossAccount,
  indent,
  vaultAccountId,
}: AwsVaultPlacement & {
  indent: string;
}) =>
  toHclComment(
    crossAccount
      ? [
          "The ARN of the KMS key that encrypts the secret. The secret is in",
          `account ${vaultAccountId}, so it needs a customer-managed key: the default`,
          "aws/secretsmanager key can't be used from another account. Set this to",
          `that key's ARN, and apply the configuration for account ${vaultAccountId}`,
          "too, which lets the connector's role read the secret there.",
        ]
      : [
          "The ARN of the KMS key that encrypts the secret, if that's a",
          "customer-managed key. The default aws/secretsmanager key needs nothing",
          "for a secret in this account.",
        ],
    indent
  );

/** The policy's lifecycle: a secret in another account than the connector's
 * needs a customer-managed key, and the plan fails until it's set. */
const renderCrossAccountPrecondition = (id: string) => `

  lifecycle {
    precondition {
      condition     = local.${id}.kms_key_arn != null
      error_message = "The OAuth client's secret is in another AWS account, so it must be encrypted with a customer-managed KMS key. Set kms_key_arn to that key's ARN."
    }
  }`;

/**
 * Renders the Terraform that lets a connector on AWS read one tailnet's
 * OAuth client secret: an inline policy on the connector's role, for the one
 * secret that P0 points the connector at.
 */
const renderAwsGrant = (
  { connectorId, tailnet }: ReadGrantArgs,
  identity: AwsIdentity,
  secret: Extract<ClientSecretPointer, { manager: "aws" }>,
  placement: AwsVaultPlacement
) => {
  const id = toTailnetTfId(tailnet);

  return `# Lets P0's Tailscale connector ${connectorId} read tailnet ${tailnet}'s
# OAuth client secret, and no other secret. Apply it in the connector's AWS
# account, ${identity.accountId}.

locals {
  ${id} = {
    # The connector's role, which the connector's own Terraform creates.
    connector_account_id = "${identity.accountId}"
    connector_role_name  = "${identity.roleName}"
    # The secret that holds ${tailnet}'s OAuth client secret, by its partial ARN: its ARN
    # without the hyphen and six random characters that end it. P0 gives the
    # connector this ARN.
    client_secret_partial_arn = "${secret.secretId}"
    secrets_region           = "${secret.region}"
    ${renderKmsKeyComment({ ...placement, indent: "    " })}
    kms_key_arn = null
  }
}

# IAM is global, so the region only has to be one you use. Add the credential
# settings your other AWS providers use, such as profile or assume_role, if
# they have any.
provider "aws" {
  alias               = "${id}"
  region              = local.${id}.secrets_region
  allowed_account_ids = [local.${id}.connector_account_id]
}

data "aws_partition" "${id}" {
  provider = aws.${id}
}

# The connector reads ${tailnet}'s OAuth client secret. Each such tailnet adds a
# policy of its own to the connector's role, and a role's inline policies share
# a limit of 10,240 characters: past a few dozen, deploy another connector for
# the rest.
resource "aws_iam_role_policy" "${id}_secret" {
  provider = aws.${id}

  name = "${toPolicyName(tailnet)}"
  role = local.${id}.connector_role_name
  policy = jsonencode({
    Version = "2012-10-17"
    Statement = concat(
      [{
        Sid    = "ReadClientSecret"
        Effect = "Allow"
        Action = "secretsmanager:GetSecretValue"
        # "??????" matches the six random characters that end the secret's
        # ARN, so the connector gets this one secret and no other.
        Resource = "\${local.${id}.client_secret_partial_arn}-??????"
      }],
      local.${id}.kms_key_arn == null ? [] : [{
        Sid      = "DecryptClientSecret"
        Effect   = "Allow"
        Action   = "kms:Decrypt"
        Resource = local.${id}.kms_key_arn
        Condition = {
          StringEquals = {
            "kms:ViaService" = "secretsmanager.\${local.${id}.secrets_region}.\${data.aws_partition.${id}.dns_suffix}"
          }
        }
      }],
    )
  })${placement.crossAccount ? renderCrossAccountPrecondition(id) : ""}
}
`;
};

/**
 * Renders the Terraform for the secret's own account, when the connector
 * runs in another: the secret's resource policy, and the statement that the
 * secret's KMS key policy needs.
 *
 * Both name the connector's role, so the role must exist first: AWS refuses
 * a policy whose principal it can't find.
 */
const renderAwsVaultAccount = (
  { connectorId, tailnet, credential }: ReadGrantArgs,
  identity: AwsIdentity,
  { install, secretsRegion }: AwsVault
) => {
  const id = `${toTailnetTfId(tailnet)}_vault`;
  const vaultAccountId = install.trim();

  return `# Tailnet ${tailnet}'s OAuth client secret is in AWS account ${vaultAccountId}, and
# P0's Tailscale connector ${connectorId} runs in account ${identity.accountId}. Apply
# this in account ${vaultAccountId}, after the connector's own Terraform. It lets the
# connector's role read the secret there.
#
# WARNING: aws_secretsmanager_secret_policy replaces the secret's whole resource
# policy. If the secret already has one, add the statement below to it where
# you manage it, and leave that resource out.

locals {
  ${id} = {
    # Where the secret that holds ${tailnet}'s OAuth client secret is.
    vault_account_id  = "${vaultAccountId}"
    secrets_region    = "${secretsRegion.trim()}"
    client_secret_name = "${credential.clientSecretName.trim()}"
    # The connector's role, which the connector's own Terraform creates.
    connector_role_arn = "${identity.roleArn}"
  }
}

# Add the credential settings your other AWS providers use, such as profile or
# assume_role, if they have any.
provider "aws" {
  alias               = "${id}"
  region              = local.${id}.secrets_region
  allowed_account_ids = [local.${id}.vault_account_id]
}

data "aws_partition" "${id}" {
  provider = aws.${id}
}

data "aws_secretsmanager_secret" "${id}" {
  provider = aws.${id}

  name = local.${id}.client_secret_name
}

resource "aws_secretsmanager_secret_policy" "${id}" {
  provider = aws.${id}

  secret_arn = data.aws_secretsmanager_secret.${id}.arn
  policy = jsonencode({
    Version = "2012-10-17"
    Statement = [{
      Sid       = "P0TailscaleConnectorReadsClientSecret"
      Effect    = "Allow"
      Principal = { AWS = local.${id}.connector_role_arn }
      Action    = "secretsmanager:GetSecretValue"
      Resource  = "*"
    }]
  })
}

# The statement to add to the policy of the customer-managed KMS key that
# encrypts the secret. Terraform can't add one statement to a key's policy,
# since aws_kms_key_policy replaces the whole policy, so add it where you
# manage the key. The default aws/secretsmanager key can't be used from another
# account, so the secret needs a key of its own.
output "${id}_kms_key_policy_statement" {
  value = {
    Sid       = "P0TailscaleConnectorDecryptsClientSecret"
    Effect    = "Allow"
    Principal = { AWS = local.${id}.connector_role_arn }
    Action    = "kms:Decrypt"
    Resource  = "*"
    Condition = {
      StringEquals = {
        "kms:ViaService" = "secretsmanager.\${local.${id}.secrets_region}.\${data.aws_partition.${id}.dns_suffix}"
      }
    }
  }
}
`;
};

/**
 * Renders the Terraform that lets a connector on Cloud Run read one
 * tailnet's OAuth client secret: two bindings on the secret itself, in the
 * secret's project, which may not be the connector's.
 *
 * The connector reads the latest version's value, then that version, so it
 * needs `secretmanager.versions.get` as well as the accessor role's
 * `secretmanager.versions.access`. The Terraform binds both roles on the
 * secret, not on its project, so the connector can read no other secret.
 */
const renderGcpGrant = (
  { connectorId, tailnet }: ReadGrantArgs,
  identity: GcpIdentity,
  secret: Extract<ClientSecretPointer, { manager: "gcp" }>
) => {
  const id = toTailnetTfId(tailnet);

  return `# Lets P0's Tailscale connector ${connectorId} read tailnet ${tailnet}'s
# OAuth client secret, and no other secret. Apply it where you manage project
# ${secret.projectId}, which holds the secret. The secret must exist first.
#
# Each binding adds one member to the secret's IAM policy, and replaces
# nothing.

locals {
  ${id} = {
    # The secret that holds ${tailnet}'s OAuth client secret, and its project.
    client_secret_project = "${secret.projectId}"
    client_secret_id      = "${secret.secretId}"
    # The connector's service account, which the connector's own Terraform
    # creates.
    connector_service_account = "${identity.serviceAccountEmail}"
  }
}

# The connector reads the OAuth client secret's value.
resource "google_secret_manager_secret_iam_member" "${id}_secret_accessor" {
  project   = local.${id}.client_secret_project
  secret_id = local.${id}.client_secret_id
  role      = "roles/secretmanager.secretAccessor"
  member    = "serviceAccount:\${local.${id}.connector_service_account}"
}

# The accessor role gives secretmanager.versions.access only. The connector also
# reads the version it got the value from, which needs
# secretmanager.versions.get.
resource "google_secret_manager_secret_iam_member" "${id}_secret_viewer" {
  project   = local.${id}.client_secret_project
  secret_id = local.${id}.client_secret_id
  role      = "roles/secretmanager.viewer"
  member    = "serviceAccount:\${local.${id}.connector_service_account}"
}
`;
};

/**
 * Renders the Terraform that lets a tailnet's connector read that tailnet's
 * OAuth client secret, and nothing else, for the customer to apply in their
 * own cloud.
 *
 * On AWS, a secret in another account than the connector's takes a second
 * configuration, applied in the secret's account.
 *
 * @returns The configurations in the order to apply them, or `undefined` when
 * the secret isn't in the connector's cloud. P0 refuses to store such an
 * item, so only a hand-edited one can be.
 */
export const renderReadGrantTerraform = (
  args: ReadGrantArgs
): ReadGrantBlock[] | undefined => {
  const { credential, hosting } = args;
  const { vault } = credential;
  const identity = toConnectorIdentity(hosting);
  const secret = toClientSecretPointer(credential);
  if (
    identity.type === "aws" &&
    secret.manager === "aws" &&
    vault.type === "aws-sm"
  ) {
    const vaultAccountId = vault.install.trim();
    const crossAccount = vaultAccountId !== identity.accountId;
    return [
      {
        purpose: "grant",
        terraform: renderAwsGrant(args, identity, secret, {
          crossAccount,
          vaultAccountId,
        }),
      },
      ...(crossAccount
        ? [
            {
              accountId: vaultAccountId,
              purpose: "vault-account" as const,
              terraform: renderAwsVaultAccount(args, identity, vault),
            },
          ]
        : []),
    ];
  }
  if (identity.type === "gcp" && secret.manager === "gcp") {
    return [
      { purpose: "grant", terraform: renderGcpGrant(args, identity, secret) },
    ];
  }
  return undefined;
};

/** Tailscale caps a federated identity's description at 50 characters. */
const DESCRIPTION_MAX_LENGTH = 50;

/** The issuer of a Google service account's ID tokens, which a federated
 * identity trusts for a connector on Cloud Run. */
const GOOGLE_ISSUER = "https://accounts.google.com";

/** The Terraform name of a federated identity for a connector's tailnets. */
const toIdentityTfId = (connectorId: string) =>
  tfId(`p0_tailscale_${connectorId}`);

const SCOPES = TAILSCALE_SCOPES.map((scope) => `    "${scope}",`).join("\n");

/** The issuer and subject lines of a federated identity that trusts the
 * connector at `hosting`, the blocks they read, and the provider those blocks
 * need, as a `required_providers` entry. */
const renderTrust = (hosting: TailscaleConnectorHosting, id: string) => {
  const identity = toConnectorIdentity(hosting);
  switch (identity.type) {
    case "aws":
      return {
        blocks: `# The issuer of this AWS account's identity tokens, which the connector's own
# Terraform turns on. Add the credential settings your other AWS providers use,
# such as profile or assume_role, if they have any.
provider "aws" {
  alias               = "${id}"
  region              = "${hosting.connectorRegion.trim()}"
  allowed_account_ids = ["${identity.accountId}"]
}

data "aws_iam_outbound_web_identity_federation" "${id}" {
  provider = aws.${id}
}
`,
        issuer: `data.aws_iam_outbound_web_identity_federation.${id}.issuer_identifier`,
        providerRequirement: `
    aws = {
      source  = "hashicorp/aws"
      version = ">= 6.36.0"
    }`,
        // A Lambda's tokens name the role it runs as.
        subject: `"${identity.roleArn}"`,
      };
    case "gcp":
      return {
        blocks: `# The connector's service account, which the connector's own Terraform creates.
data "google_service_account" "${id}" {
  project    = "${identity.projectId}"
  account_id = "${identity.serviceAccountId}"
}
`,
        issuer: `"${GOOGLE_ISSUER}"`,
        providerRequirement: "",
        // A Google ID token's subject is the service account's numeric unique
        // ID, not its email.
        subject: `data.google_service_account.${id}.unique_id`,
      };
    default:
      throw assertNever(identity);
  }
};

/**
 * Renders the Terraform that creates a Tailscale federated identity trusting
 * the connector `connectorId` at `hosting`, with the scopes P0 needs, for
 * the customer to apply with Tailscale's provider signed in to the tailnet.
 * Its client ID is what the tailnet's credential takes in P0.
 *
 * It leaves the audience unset: Tailscale then generates
 * `api.tailscale.com/<client ID>`, which is what the connector asks for.
 */
export const renderFederatedIdentityTerraform = ({
  connectorId,
  hosting,
}: {
  connectorId: string;
  hosting: TailscaleConnectorHosting;
}) => {
  const id = toIdentityTfId(connectorId);
  const { blocks, issuer, providerRequirement, subject } = renderTrust(
    hosting,
    id
  );
  const description = `P0 connector ${connectorId}`.slice(
    0,
    DESCRIPTION_MAX_LENGTH
  );

  return `# A Tailscale federated identity that trusts P0's Tailscale connector
# ${connectorId}, with the scopes a tailnet needs. Apply it with Tailscale's
# provider signed in to the tailnet you're adding, then add the tailnet in P0
# with the client ID it outputs. No secret is created or stored.

terraform {
  # If your configuration already has a required_providers block, add these to
  # it and delete this block.
  required_providers {
    tailscale = {
      source  = "tailscale/tailscale"
      version = "~> 0.29.2"
    }${providerRequirement}
  }
}

${blocks}
resource "tailscale_federated_identity" "${id}" {
  description = "${description}"
  issuer      = ${issuer}
  subject     = ${subject}
  # Leave the audience unset: P0 asks for Tailscale's default one.
  scopes = [
${SCOPES}
  ]
}

# The client ID to enter in P0's tailnet form.
output "${id}_client_id" {
  value = tailscale_federated_identity.${id}.id
}
`;
};
